import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Divider, Stack } from '@mui/material'

import { LanguageMaps } from '../../../../api/language'
import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalImageUpload } from '../../../../components/InternalImageUpload/InternalImageUpload'
import {
  ConventionalItemFormValues,
  ConventionalItemRow,
  conventionalLanguages,
  conventionalTypeLabelKeys,
  maxExampleGames,
  toConventionalFormValues,
  toConventionalItemMutation,
} from '../../helpers/conventionalGenreHelpers'
import { ConventionalItemMutation, ConventionalItemType } from '../../types/InternalConventionalItem'
import { ExampleGamesField } from '../ExampleGamesField/ExampleGamesField'

type ConventionalItemEditDialogProps = {
  row?: ConventionalItemRow
  languageMaps?: LanguageMaps
  isSaving?: boolean
  onClose: () => void
  onSave: (item: ConventionalItemMutation) => void
}

const emptyValues: ConventionalItemFormValues = { names: {}, descriptions: {}, imageTexts: {}, imageUrl: '', exampleAppIds: [] }

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const ConventionalItemEditDialog: FC<ConventionalItemEditDialogProps> = ({ row, languageMaps, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<ConventionalItemFormValues>({
    mode: 'onChange',
    defaultValues: row ? toConventionalFormValues(row, languageMaps) : emptyValues,
  })

  useEffect(() => {
    if (row) reset(toConventionalFormValues(row, languageMaps))
  }, [row, languageMaps, reset])

  const isSubgenre = row?.type === ConventionalItemType.Subgenre
  const hasDescription = !!row && row.type !== ConventionalItemType.Category
  const submit = handleSubmit((values) => row && onSave(toConventionalItemMutation(row, values)))

  return (
    <InternalFormDialog
      open={!!row}
      title={row && t('internal-service-management:conventional_genres_edit', { type: t(conventionalTypeLabelKeys[row.type]), name: row.item.name })}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
      maxWidth="md"
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:conventional_genres_name')}
              dialogTitle={t('internal-service-management:conventional_genres_name')}
              selectedLanguages={conventionalLanguages}
            />
          )}
        />
        {hasDescription && (
          <Controller
            name="descriptions"
            control={control}
            render={({ field }) => (
              <InputTranslator
                value={field.value}
                onChange={field.onChange}
                label={t('internal-service-management:conventional_genres_description')}
                dialogTitle={t('internal-service-management:conventional_genres_description')}
                selectedLanguages={conventionalLanguages}
                multiline
                maxRows={6}
              />
            )}
          />
        )}
        {isSubgenre && (
          <>
            <Divider />
            <Controller
              name="imageUrl"
              control={control}
              render={({ field }) => (
                <InternalImageUpload
                  value={field.value}
                  onChange={field.onChange}
                  type="screenshot"
                  label={t('internal-service-management:conventional_genres_image')}
                  previewVariant="contain"
                  previewSize={120}
                  removable
                />
              )}
            />
            <Controller
              name="imageTexts"
              control={control}
              render={({ field }) => (
                <InputTranslator
                  value={field.value}
                  onChange={field.onChange}
                  label={t('internal-service-management:conventional_genres_image_text')}
                  dialogTitle={t('internal-service-management:conventional_genres_image_text')}
                  selectedLanguages={conventionalLanguages}
                />
              )}
            />
            <Divider />
            <Controller
              name="exampleAppIds"
              control={control}
              render={({ field }) => <ExampleGamesField value={field.value} onChange={field.onChange} max={maxExampleGames} />}
            />
          </>
        )}
      </Stack>
    </InternalFormDialog>
  )
}
